import { useMemo, useState, cloneElement } from 'react'
import SparklineGraph from '../../../components/Graphs/SparklineGraph'
import { Button, Typography, Paper } from '@mui/material'
import { BarChart, ShowChart, OpenInFull, CloseFullscreen } from '@mui/icons-material'
import useReportGraph from '../hooks/useReportGraph'
import Modal from '../../../components/Modal'
import { aggregationOptions as options } from '../../../utils/aggregationUtils'
import Tooltip from '../../../components/Tooltip'
import ReportpageBarGroup from './ReportpageBarGroup'
import ReportGraphLegend from './ReportGraphLegend'
import { useReportGraphContext } from '../context/useReportGraph'
import { NoGraph } from './utils'
import styles from './ReportGraph.module.scss'

const getName = (value, options) => options.find(option => option.value === value)?.name

const ReportPageGraph = ({data, graphType, columnName, datasetName, aggregationTime, aggregationType}) => {
    const [ openModal, setOpenModal ] = useState(false)
    const [ type, setType ] = useState(graphType) 
    const { handleUpdateGraphType } = useReportGraph(data.dataset, data.column, data.ticker)
    const { selectedGraphs } = useReportGraphContext()

    const handleClick = (value) => {
        handleUpdateGraphType(value)
        setType(value)
    }

    const handleClickModal = () => setOpenModal(state => !state)
    const handleCloseModal = () => setOpenModal(false)
   
    const buttonsOptions = {
        line: <ShowChart/>,
        bar: <BarChart/>
    }

    const buttons = useMemo(() => Object.keys(buttonsOptions)
        .map(key => <GraphButton 
            key={key}
            value={key}
            icon={buttonsOptions[key]} 
            handleClick={handleClick} 
            type={type}/>
    ),[type]) // eslint-disable-line

    const graph = useMemo(() => (
        <>
            <div className={styles['graph-buttons']}>
                <ReportGraphLegend/>
                <div>
                    <div>
                        {buttons}
                    </div>
                    <Button
                        size='small'
                        variant='outlined'
                        classes={{root: styles['open-close-btn']}}
                        onClick={handleClickModal}>
                        {!openModal 
                            ? <OpenInFull color='textSecondary' fontSize='small'/>
                            : <CloseFullscreen color='textSecondary' fontSize='small' />
                        }
                    </Button>
                </div>
            </div>
            <div className={styles['graph']}>
                { type === 'line' 
                    ? <LineGraph data={selectedGraphs}/> 
                    : <ReportpageBarGroup graphs={selectedGraphs}/>
                }
            </div>
        </>
    ),[selectedGraphs, type, buttons, openModal])

    const subTilte = useMemo(() => (
        <Typography 
            variant="body2" 
            color="textSecondary">
                Aggregation by {getName(aggregationTime, options.group)?.toLowerCase()}, {getName(aggregationType, options.agg)?.toLowerCase()}
        </Typography>
    ),[aggregationTime, aggregationType])

    return (
        <>
            <div className={styles['graph-container']}>
                <div className={styles['title']}>
                    <Tooltip title={`${datasetName}: ${columnName}`} placement="top-start">
                        <Typography variant="h2">{datasetName}: {columnName}</Typography>
                    </Tooltip>
                    {subTilte}
                </div>
                <Paper className={styles['content']} elevation={3}>
                    {data?.data?.length ? graph : <NoGraph/>}
                </Paper>
            </div>
            <Modal open={openModal} limitWidth={false} hideHeader handleClose={handleCloseModal}>
                <div className={styles['modal-graph']}>
                    {graph}
                </div>
            </Modal>
        </>
    )
}

const LineGraph = ({data}) => {
    return  <SparklineGraph showAxisBottom showAxisLeft graph={data}/>
}

const GraphButton = ({value, handleClick, icon, type}) => {

    const onClick = () => handleClick(value)

    return (
        <Button
            size='small'
            variant='outlined' 
            classes={{root: `${styles['graph-button']} ${styles[type === value ? 'graph-active-button' : '']}`}} 
            onClick={onClick}>
                {cloneElement(icon, {fontSize:'small'})}
        </Button>
    )
}

export default ReportPageGraph